import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useApi } from "@/hooks/useApi";
import { useNow } from "@/hooks/useNow";
import { useAuth } from "@/components/feature/AuthProvider";
import type { TugBrief, TugTeam } from "@/lib/types";
import { SkeletonCard } from "@/components/base/Skeleton";
import TugCard from "@/pages/tug/components/TugCard";
import TugWriteModal from "@/pages/tug/components/TugWriteModal";
import GameSectionEmpty from "@/components/feature/GameSectionEmpty";
import type { GamePastItem } from "@/components/feature/GamePastStrip";

// 메인 홈 «줄다리기» 섹션.
// 가위바위보·내가쏜다처럼 «진행중 경기가 없어도 섹션은 항상 보인다».
// 열린 경기가 없으면 왼쪽 안내+등록 버튼, 오른쪽 지난 경기 썸네일 몇 개만 간단히 표시한다.
export default function TugSection() {
  const { data, loading, serverTs, reload } = useApi<TugBrief[]>("tug.php");
  const now = useNow();
  const { member, requireLogin } = useAuth();
  const [writeOpen, setWriteOpen] = useState(false);

  const list = data ?? [];
  const { open, past } = useMemo(
    () => ({
      open: list.filter((r) => r.state === "open"),
      past: list.filter((r) => r.state !== "open"),
    }),
    [data]
  );

  const elapsed = serverTs ? Math.max(0, now / 1000 - serverTs) : 0;
  const scroll = open.length >= 3;

  const openWrite = () => {
    if (!member) {
      requireLogin("/tug");
      return;
    }
    setWriteOpen(true);
  };

  const handleCreated = (no?: number, team?: TugTeam) => {
    setWriteOpen(false);
    reload();
    if (no) {
      window.setTimeout(() => {
        document
          .getElementById(`tug-${no}`)
          ?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 350);
    }
  };

  const pastItems: GamePastItem[] = past.slice(0, 5).map((r) => ({
    no: r.no,
    to: `/tug/${r.no}`,
    icon: "🪢",
    title: r.subject,
    meta:
      r.win_team === "D"
        ? "무승부"
        : `${r.win_name || "?"} 승리`,
  }));

  return (
    <section className="w-full bg-background-100 px-4 py-14 md:px-6 md:py-16">
      <div className="mx-auto max-w-6xl">
        <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
          <div>
            <h2 className="flex items-center gap-2 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              <span aria-hidden="true">🪢</span>
              단체 줄다리기
            </h2>
            <p className="mt-2 text-foreground-600">
              두 팀으로 나눠 화면을 두드리는 팀 대항 게임. 점수는 종료 후 공개됩니다.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={openWrite}
              className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600"
            >
              <i className="ri-add-line text-base" aria-hidden="true"></i>
              경기 열기
            </button>
            <Link
              to="/tug"
              className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border border-primary-200 bg-primary-50 px-5 py-2.5 text-sm font-semibold text-primary-700 transition-colors hover:bg-primary-100"
            >
              전체 보기
              <i className="ri-arrow-right-line" aria-hidden="true"></i>
            </Link>
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        ) : open.length === 0 ? (
          <GameSectionEmpty
            icon="ri-drag-move-2-line"
            emptyTitle="지금 열린 경기가 없습니다."
            emptyDesc="첫 번째 경기를 열어보세요!"
            registerLabel="경기 열기"
            onRegister={openWrite}
            pastItems={pastItems}
            pastTitle="지난 경기"
            pastEmptyText="아직 끝난 경기가 없습니다."
          />
        ) : scroll ? (
          <div className="-mx-4 flex gap-5 overflow-x-auto px-4 pb-2 md:mx-0 md:px-0">
            {open.map((r) => (
              <div key={r.no} className="w-[320px] shrink-0">
                <TugCard brief={r} elapsed={elapsed} />
              </div>
            ))}
          </div>
        ) : (
          <div
            className={`grid grid-cols-1 gap-5 ${
              open.length > 1 ? "md:grid-cols-2" : ""
            }`}
          >
            {open.map((r) => (
              <TugCard key={r.no} brief={r} elapsed={elapsed} />
            ))}
          </div>
        )}
      </div>

      <TugWriteModal
        open={writeOpen}
        onClose={() => setWriteOpen(false)}
        onCreated={handleCreated}
      />
    </section>
  );
}